import { Chess } from 'chess.js';

const FILES = 'abcdefgh';

function getMoveHistory(positionHistory) {
  if (!Array.isArray(positionHistory)) return [];

  const moveHistory = [];

  for (let index = 1; index < positionHistory.length; index += 1) {
    const previousPosition = positionHistory[index - 1];
    const currentPosition = positionHistory[index];
    const previousFen = typeof previousPosition === 'string'
      ? previousPosition
      : previousPosition && previousPosition.fen;
    const move = currentPosition && currentPosition.moves
      ? currentPosition.moves[0]
      : null;

    if (!previousFen || !move) continue;

    const previousGame = new Chess(previousFen);
    const piece = previousGame.get(move[0]);
    if (!piece) continue;

    moveHistory.push({
      color: piece.color,
      pieceType: piece.type,
      sourceSquare: move[0],
      targetSquare: move[1],
    });
  }

  return moveHistory;
}

function findKing(game, color) {
  for (const file of FILES) {
    for (let rank = 1; rank <= 8; rank += 1) {
      const square = `${file}${rank}`;
      const piece = game.get(square);
      if (piece && piece.type === 'k' && piece.color === color) {
        return square;
      }
    }
  }

  return null;
}

function isNextToOwnKingOrQueen(game, color, square) {
  const sourceFile = FILES.indexOf(square[0]);
  const sourceRank = Number(square[1]);

  for (let fileOffset = -1; fileOffset <= 1; fileOffset += 1) {
    for (let rankOffset = -1; rankOffset <= 1; rankOffset += 1) {
      if (fileOffset === 0 && rankOffset === 0) continue;

      const file = FILES[sourceFile + fileOffset];
      const rank = sourceRank + rankOffset;
      if (!file || rank < 1 || rank > 8) continue;

      const piece = game.get(`${file}${rank}`);
      if (
        piece
        && piece.color === color
        && (piece.type === 'k' || piece.type === 'q')
      ) {
        return true;
      }
    }
  }

  return false;
}

function movedQueenChecksOpponentKing({
  game,
  movingPiece,
  sourceSquare,
  targetSquare,
}) {
  if (!movingPiece || movingPiece.type !== 'q') return false;

  const opponentColor = movingPiece.color === 'w' ? 'b' : 'w';
  const kingSquare = findKing(game, opponentColor);
  if (!kingSquare) return false;

  const targetFile = FILES.indexOf(targetSquare[0]);
  const targetRank = Number(targetSquare[1]);
  const kingFile = FILES.indexOf(kingSquare[0]);
  const kingRank = Number(kingSquare[1]);
  const fileDistance = kingFile - targetFile;
  const rankDistance = kingRank - targetRank;
  const sharesQueenLine = (
    fileDistance === 0
    || rankDistance === 0
    || Math.abs(fileDistance) === Math.abs(rankDistance)
  );

  if (!sharesQueenLine) return false;

  const fileStep = Math.sign(fileDistance);
  const rankStep = Math.sign(rankDistance);
  let file = targetFile + fileStep;
  let rank = targetRank + rankStep;

  while (file !== kingFile || rank !== kingRank) {
    const square = `${FILES[file]}${rank}`;
    if (square !== sourceSquare && game.get(square)) return false;

    file += fileStep;
    rank += rankStep;
  }

  return true;
}

const BOOK_MOVES = [
  {
    name: 'Goatse opening',
    conditions: [
      ({ playerMoveNumber }) => playerMoveNumber === 1,
      ({ movingPiece }) => movingPiece && movingPiece.type === 'p',
      ({ movingPiece, game, sourceSquare }) => {
        if (!movingPiece) return false;

        const kingSquare = findKing(game, movingPiece.color);
        if (!kingSquare) return false;

        const direction = movingPiece.color === 'w' ? 1 : -1;
        return (
          sourceSquare[0] === kingSquare[0]
          && Number(sourceSquare[1]) === Number(kingSquare[1]) + direction
        );
      },
      ({ movingPiece, sourceSquare, targetSquare }) => {
        if (!movingPiece) return false;

        const direction = movingPiece.color === 'w' ? 1 : -1;
        return (
          targetSquare[0] === sourceSquare[0]
          && Number(targetSquare[1]) === Number(sourceSquare[1]) + (2 * direction)
        );
      },
    ],
  },
  {
    name: 'Royal divorce',
    conditions: [
      ({ playerMoveNumber }) => (
        playerMoveNumber >= 1 && playerMoveNumber <= 3
      ),
      ({ movingPiece }) => movingPiece && movingPiece.type === 'q',
      ({ movingPiece, sourceSquare, targetSquare }) => {
        if (!movingPiece) return false;

        if (movingPiece.color === 'w') {
          return sourceSquare === 'd1' && targetSquare === 'g4';
        }

        return sourceSquare === 'd8' && targetSquare === 'g5';
      },
    ],
  },
  {
    name: 'Side Piece discreet exit',
    conditions: [
      ({ movingPiece }) => movingPiece && movingPiece.type === 'b',
      ({ movingPiece, game, sourceSquare }) => (
        movingPiece
        && isNextToOwnKingOrQueen(game, movingPiece.color, sourceSquare)
      ),
      ({ sourceSquare, targetSquare }) => {
        const fileDistance = Math.abs(
          FILES.indexOf(targetSquare[0]) - FILES.indexOf(sourceSquare[0])
        );
        const rankDistance = Math.abs(
          Number(targetSquare[1]) - Number(sourceSquare[1])
        );

        return fileDistance === rankDistance && fileDistance > 1;
      },
    ],
  },
  {
    name: 'Trump’s Iran Attack',
    conditions: [
      ({ playerMoveNumber }) => (
        playerMoveNumber >= 1 && playerMoveNumber <= 4
      ),
      ({ movingPiece }) => movingPiece && movingPiece.type === 'q',
      ({ movingPiece, targetSquare }) => (
        (movingPiece.color === 'w' && targetSquare === 'e6')
        || (movingPiece.color === 'b' && targetSquare === 'e3')
      ),
      movedQueenChecksOpponentKing,
    ],
  },
  {
    name: 'Main Character Syndrome',
    conditions: [
      ({ playerMoveNumber }) => (
        playerMoveNumber >= 1 && playerMoveNumber <= 5
      ),
      ({ movingPiece }) => movingPiece && movingPiece.type === 'q',
      ({ movingPiece, moveHistory }) => (
        moveHistory.filter(
          (move) => (
            move.color === movingPiece.color && move.pieceType === 'q'
          )
        ).length === 2
      ),
    ],
  },
  {
    name: 'Going for cigarettes',
    conditions: [
      ({ movingPiece }) => movingPiece && movingPiece.type === 'k',
      ({ movingPiece, sourceSquare, targetSquare }) => (
        (
          movingPiece.color === 'w'
          && sourceSquare === 'e1'
          && targetSquare === 'g1'
        )
        || (
          movingPiece.color === 'b'
          && sourceSquare === 'e8'
          && targetSquare === 'g8'
        )
      ),
    ],
  },
  {
    name: 'Going for milk',
    conditions: [
      ({ movingPiece }) => movingPiece && movingPiece.type === 'k',
      ({ movingPiece, sourceSquare, targetSquare }) => (
        (
          movingPiece.color === 'w'
          && sourceSquare === 'e1'
          && targetSquare === 'c1'
        )
        || (
          movingPiece.color === 'b'
          && sourceSquare === 'e8'
          && targetSquare === 'c8'
        )
      ),
    ],
  },
];

export function findBookMove({
  game,
  sourceSquare,
  targetSquare,
  playerMoveNumber,
  positionHistory = [],
}) {
  if (!game) return null;

  const context = {
    game,
    sourceSquare,
    targetSquare,
    playerMoveNumber,
    movingPiece: game.get(sourceSquare),
    moveHistory: getMoveHistory(positionHistory),
  };

  return BOOK_MOVES.find(
    (bookMove) => bookMove.conditions.every((condition) => condition(context))
  ) || null;
}

export { BOOK_MOVES };
